import { useState, useCallback, useMemo } from 'react';
import {
  Grid,
  Divider,
  PanelContent,
  SearchableTextField,
  useTranslations,
  FilterButton,
  Tooltip,
  usePopper,
  useMapKeyValueExtractor,
  ActionButton,
  ShareIcon,
  ErrorIcon,
  DeleteIcon,
  useHeroSnackbar,
  PromptModal,
  ISearchableTextFieldProps,
  IFilterButtonProps,
  encryptSymmetric,
  Box,
  Button,
  PlusIcon,
  MoveIcon,
  MoveToEmployeeIcon,
  MoveToGroupIcon,
  Typography,
  SeverityEnum,
} from '@uniqkey-frontend/shared-app';
import { Ownership } from '@uniqkey-backend-organization-web/api-client';
import useOrganizationCreditCardsTable, {
  IOrganizationCreditCardsTableRow,
} from '../../../../hooks/tables/useOrganizationCreditCardsTable';
import OrganizationCreditCardsTable
  from '../../../../components/tables/OrganizationCreditCardsTable';
import OrganizationCreditCardsTabFilter, {
  IOrganizationCreditCardsTabFilterSubmitResult,
} from './components/OrganizationCreditCardsTabFilter';
import { useDeleteVaults, useDetachVaults } from '../../../../hooks/reactQuery';
import ShareVaultsToGroupModal from '../../../../components/ShareVaultsToGroupModal';
import VaultTypeEnum from '../../../../enums/VaultTypeEnum';
import { logException } from '../../../../services/sentryService';
import CreateCreditCardModal, {
  ICreateCreditCardReturnValue,
} from '../../../../components/CreateCreditCardModal';
import { useCompanionApplicationContext } from '../../../../contexts/CompanionApplicationContext';
import useVaultsAPI from '../../../../hooks/useVaultsAPI';
import { getActiveOrganizationId } from '../../../../services/organizationService';
import { useTrustedPortalStore } from '../../../../modules/TrustedPortalModule/store';
import MoveVaultsToEmployeeModal from '../../../../components/MoveVaultsToEmployeeModal';
import MoveVaultsToGroupModal from '../../../../components/MoveVaultsToGroupModal';
import { getTranslationKeyByError } from '../../../../helpers/errorService';

const OrganizationCreditCardsTab = () => {
  const [isShareCreditCardsModalOpen, setIsShareCreditCardsModalOpen] = useState(false);
  const [isDetachCreditCardsModalOpen, setIsDetachCreditCardsModalOpen] = useState(false);
  const [isDeleteCreditCardsModalOpen, setIsDeleteCreditCardsModalOpen] = useState(false);
  const [isCreateCreditCardModalOpen, setIsCreateCreditCardModalOpen] = useState(false);
  const [isCreateCreditCardLoading, setIsCreateCreditCardLoading] = useState(false);
  const [isMoveToPrivateCreditCardsModalOpen, setIsMoveToPrivateCreditCardsModalOpen] = useState(
    false,
  );
  const [isMoveToPrivateCreditCardsLoading, setIsMoveToPrivateCreditCardsLoading] = useState(false);
  const [isMoveToEmployeeCreditCardsModalOpen, setIsMoveToEmployeeCreditCardsModalOpen] = useState(
    false,
  );
  const [isMoveToGroupCreditCardsModalOpen, setIsMoveToGroupCreditCardsModalOpen] = useState(
    false,
  );

  const { t } = useTranslations();
  const {
    isOpen: isFilterOpen,
    anchorEl: filterAnchorEl,
    setPopperIsOpen: setIsFilterOpen,
  } = usePopper();
  const { showError, showSuccess, showWarning } = useHeroSnackbar();
  const { symmetricKey } = useCompanionApplicationContext();
  const { createVaultCreditCard, moveVaultsToPrivateKeychain } = useVaultsAPI();

  const {
    selectedCreditCards,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps
  } = useOrganizationCreditCardsTable({
    noDataMessageKey: 'organizationCreditCardsTab.noData',
  });

  const {
    values: selectedCreditCardsAsObjects, keys: selectedCreditCardsIds,
  } = useMapKeyValueExtractor<IOrganizationCreditCardsTableRow>(selectedCreditCards);
  const activeOrganizationId = getActiveOrganizationId();
  const isTrustedPortalEnabled = useTrustedPortalStore.useIsEnabledByOrganizationId()[
    activeOrganizationId!
  ] ?? false;
  const {
    mutate: mutateDetachCreditCard, isLoading: isDetachCreditCardsLoading,
  } = useDetachVaults();
  const {
    mutate: mutateDeleteCreditCard, isLoading: isDeleteCreditCardsLoading,
  } = useDeleteVaults();

  const toggleIsFilterOpen = useCallback<NonNullable<IFilterButtonProps['onChange']>>(
    (event) => setIsFilterOpen(!isFilterOpen, event),
    [setIsFilterOpen, isFilterOpen],
  );
  const handleFilterClose = useCallback(
    () => setIsFilterOpen(false),
    [setIsFilterOpen],
  );
  const handleDetachCreditCardsModalOpen = useCallback(
    () => setIsDetachCreditCardsModalOpen(true),
    [],
  );
  const handleDetachCreditCardsModalClose = useCallback(
    () => setIsDetachCreditCardsModalOpen(false),
    [],
  );
  const handleDeleteCreditCardsModalOpen = useCallback(
    () => setIsDeleteCreditCardsModalOpen(true),
    [],
  );
  const handleDeleteCreditCardsModalClose = useCallback(
    () => setIsDeleteCreditCardsModalOpen(false),
    [],
  );
  const handleShareCreditCardsModalOpen = useCallback(
    () => setIsShareCreditCardsModalOpen(true),
    [],
  );
  const handleShareCreditCardsModalClose = useCallback(
    () => setIsShareCreditCardsModalOpen(false),
    [],
  );
  const handleCreateCreditCardModalOpen = useCallback(
    () => setIsCreateCreditCardModalOpen(true),
    [],
  );
  const handleCreateCreditCardModalClose = useCallback(
    () => setIsCreateCreditCardModalOpen(false),
    [],
  );
  const handleMoveToPrivateCreditCardsModalOpen = useCallback(
    () => setIsMoveToPrivateCreditCardsModalOpen(true),
    [],
  );
  const handleMoveToPrivateCreditCardsModalClose = useCallback(
    () => setIsMoveToPrivateCreditCardsModalOpen(false),
    [],
  );
  const handleMoveToEmployeeCreditCardsModalOpen = useCallback(
    () => setIsMoveToEmployeeCreditCardsModalOpen(true),
    [],
  );
  const handleMoveToEmployeeCreditCardsModalClose = useCallback(
    () => setIsMoveToEmployeeCreditCardsModalOpen(false),
    [],
  );
  const handleMoveToGroupCreditCardsModalOpen = useCallback(
    () => setIsMoveToGroupCreditCardsModalOpen(true),
    [],
  );
  const handleMoveToGroupCreditCardsModalClose = useCallback(
    () => setIsMoveToGroupCreditCardsModalOpen(false),
    [],
  );

  const handleSearchChange = useCallback<ISearchableTextFieldProps['onChange']>(
    (debouncedValue) => {
      setSearchQuery(debouncedValue);
      resetActivePage();
    },
    [setSearchQuery, resetActivePage],
  );

  const handleFilterSubmit = useCallback((
    updatedValues: IOrganizationCreditCardsTabFilterSubmitResult,
  ) => {
    setFilterValues(updatedValues);
    resetActivePage();
  }, [setFilterValues, resetActivePage]);

  const handleCreateCreditCard = useCallback(async (params: ICreateCreditCardReturnValue) => {
    const {
      name, cardNumber, expiryDate, cvv, cardHolderName,
    } = params;
    setIsCreateCreditCardLoading(true);
    try {
      await createVaultCreditCard({
        name,
        cardNumber: encryptSymmetric({
          key: symmetricKey,
          string: cardNumber,
        }),
        expiryDate: encryptSymmetric({
          key: symmetricKey,
          string: expiryDate,
          fallbackValue: '',
        }),
        cardHolderName: encryptSymmetric({
          key: symmetricKey,
          string: cardHolderName,
          fallbackValue: '',
        }),
        cvv: encryptSymmetric({
          key: symmetricKey,
          string: cvv,
          fallbackValue: '',
        }),
      });
      if (isTrustedPortalEnabled) {
        showSuccess({
          text: t('trustedPortalSuccessNotifications.creditCard.created'),
        });
      } else {
        showWarning({
          text: t('createCreditCardModal.approvalOnMobileToast'),
        });
      }
      handleCreateCreditCardModalClose();
    } catch (e) {
      showError({
        text: t('common.somethingWentWrong'),
      });
      logException(e, {
        message: 'OrganizationCreditCardsTab/handleCreateCreditCard exception',
      });
    } finally {
      setIsCreateCreditCardLoading(false);
    }
  }, [
    createVaultCreditCard,
    handleCreateCreditCardModalClose,
    symmetricKey,
    showError,
    showSuccess,
    showWarning,
    isTrustedPortalEnabled,
    t,
  ]);

  const handleDetachCreditCard = useCallback(async () => {
    mutateDetachCreditCard({
      vaultIds: selectedCreditCardsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationCreditCardsTab.detachCreditCardModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t('organizationCreditCardsTab.detachCreditCardModal.errorMessage', {
              count: failCount,
            }),
          });
        }
        handleDetachCreditCardsModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, {
          message: 'OrganizationCreditCardsTab/handleDetachCreditCard exception',
        });
      },
    });
  }, [
    selectedCreditCardsIds,
    mutateDetachCreditCard,
    handleDetachCreditCardsModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const handleDeleteCreditCard = useCallback(async () => {
    mutateDeleteCreditCard({
      vaultIds: selectedCreditCardsIds,
    }, {
      onSuccess: ({ failCount, successCount }) => {
        if (successCount) {
          showSuccess({
            text: t(
              'organizationCreditCardsTab.deleteCreditCardModal.successMessage',
              { count: successCount },
            ),
          });
        }
        if (failCount) {
          showError({
            text: t(
              'organizationCreditCardsTab.deleteCreditCardModal.errorMessage',
              { count: failCount },
            ),
          });
        }
        handleDeleteCreditCardsModalClose();
        resetSelectedRows();
        resetActivePage();
      },
      onError: (e) => {
        showError({ text: t(getTranslationKeyByError(e)) });
        logException(e, {
          message: 'OrganizationCreditCardsTab/handleDeleteCreditCard exception',
        });
      },
    });
  }, [
    selectedCreditCardsIds,
    mutateDeleteCreditCard,
    handleDeleteCreditCardsModalClose,
    resetSelectedRows,
    resetActivePage,
    showSuccess,
    showError,
    t,
  ]);

  const {
    isMoveToPrivateDisabled,
    isMoveToEmployeeDisabled,
    isMoveToGroupDisabled,
    isShareDisabled,
    isRemoveDisabled,
    isDeleteDisabled,
    moveToPrivateTooltipTitle,
    moveToEmployeeTooltipTitle,
    moveToGroupTooltipTitle,
    shareTooltipTitle,
    removeTooltipTitle,
    deleteTooltipTitle,
  } = useMemo(() => {
    if (!selectedCreditCardsAsObjects.length) {
      return {
        isMoveToPrivateDisabled: true,
        isMoveToEmployeeDisabled: true,
        isMoveToGroupDisabled: true,
        isShareDisabled: true,
        isRemoveDisabled: true,
        isDeleteDisabled: true,
        moveToPrivateTooltipTitle: t('common.tickRow'),
        moveToEmployeeTooltipTitle: t('common.tickRow'),
        moveToGroupTooltipTitle: t('common.tickRow'),
        shareTooltipTitle: t('common.tickRow'),
        removeTooltipTitle: t('common.tickRow'),
        deleteTooltipTitle: t('common.tickRow'),
      };
    }
    let disableShare = false;
    let disableRemove = false;
    let disableDelete = false;
    let disableMoveToPrivate = false;
    let moveToPrivateTitle = t('organizationCreditCardsTab.moveToPrivate.active');
    let shareTitle = t('organizationCreditCardsTab.shareToGroup.active');
    let removeTitle = t('organizationCreditCardsTab.remove.active');
    let deleteTitle = t('organizationCreditCardsTab.delete.active');
    selectedCreditCardsAsObjects.forEach(({ ownership }) => {
      if (ownership === Ownership.Unmanaged) {
        disableRemove = true;
        removeTitle = t('organizationCreditCardsTab.remove.disabled');
        disableShare = true;
        shareTitle = t('organizationCreditCardsTab.shareToGroup.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationCreditCardsTab.moveToPrivate.disabled');
      }
      if (ownership === Ownership.Employees) {
        disableShare = true;
        shareTitle = t('organizationCreditCardsTab.shareToGroup.disabled');
        disableDelete = true;
        deleteTitle = t('organizationCreditCardsTab.delete.disabled');
      }
      if (ownership === Ownership.Groups) {
        disableDelete = true;
        deleteTitle = t('organizationCreditCardsTab.delete.disabled');
        disableMoveToPrivate = true;
        moveToPrivateTitle = t('organizationCreditCardsTab.moveToPrivate.disabled');
      }
    });
    const uniqueOwnershipCount = new Set(selectedCreditCardsAsObjects.map(
      ({ ownership }) => ownership,
    )).size;
    if (uniqueOwnershipCount > 1) {
      disableShare = true;
      disableRemove = true;
      disableDelete = true;
      disableMoveToPrivate = true;
      shareTitle = t('common.valuesInManagedByShouldBeIdentical');
      removeTitle = t('common.valuesInManagedByShouldBeIdentical');
      deleteTitle = t('common.valuesInManagedByShouldBeIdentical');
      moveToPrivateTitle = t('common.valuesInManagedByShouldBeIdentical');
    }
    return {
      isMoveToPrivateDisabled: disableMoveToPrivate,
      isMoveToEmployeeDisabled: false,
      isMoveToGroupDisabled: false,
      isShareDisabled: disableShare,
      isRemoveDisabled: disableRemove,
      isDeleteDisabled: disableDelete,
      moveToPrivateTooltipTitle: moveToPrivateTitle,
      moveToEmployeeTooltipTitle: t('organizationCreditCardsTab.moveToEmployee.active'),
      moveToGroupTooltipTitle: t('organizationCreditCardsTab.moveToGroup.active'),
      shareTooltipTitle: shareTitle,
      removeTooltipTitle: removeTitle,
      deleteTooltipTitle: deleteTitle,
    };
  }, [selectedCreditCardsAsObjects, t]);

  const ownership = useMemo(() => {
    if (selectedCreditCardsAsObjects.length) {
      return selectedCreditCardsAsObjects[0].ownership;
    }
    return null;
  }, [selectedCreditCardsAsObjects]);

  const handleMoveToPrivateCreditCards = useCallback(async () => {
    try {
      setIsMoveToPrivateCreditCardsLoading(true);
      await moveVaultsToPrivateKeychain({ vaultIds: selectedCreditCardsIds });
      if (isTrustedPortalEnabled) {
        showSuccess({ text: t('trustedPortalSuccessNotifications.creditCard.moved') });
      } else {
        showWarning({ text: t('moveCreditCardsModal.approvalOnMobileToast') });
      }
      resetSelectedRows();
      handleMoveToPrivateCreditCardsModalClose();
    } catch (e) {
      showError({ text: t(getTranslationKeyByError(e)) });
      logException(e, {
        message: 'OrganizationCreditCardsTab/handleMoveToPrivateCreditCards exception',
      });
    } finally {
      setIsMoveToPrivateCreditCardsLoading(false);
    }
  }, [
    moveVaultsToPrivateKeychain,
    selectedCreditCardsIds,
    isTrustedPortalEnabled,
    showWarning,
    showError,
    showSuccess,
    handleMoveToPrivateCreditCardsModalClose,
    resetSelectedRows,
    t,
  ]);

  const moveToPrivateCreditCardsModalDescriptionElement = useMemo(
    () => (
      <>
        <Typography>{t('moveCreditCardsToPrivateKeychainModal.description')}</Typography>
        <Typography>{t('moveCreditCardsToPrivateKeychainModal.additionalDescription')}</Typography>
      </>
    ),
    [t],
  );

  return (
    <PanelContent p={0}>
      <Grid container justifyContent="space-between" alignItems="stretch" p={1}>
        <Grid item xs={4} container flexWrap="nowrap" spacing={1}>
          <Grid item>
            <Tooltip title={t('common.filter')}>
              <FilterButton
                isFilterActive={isFilterActive}
                numberOfActiveFilters={numberOfActiveFilters}
                selected={isFilterOpen}
                onChange={toggleIsFilterOpen}
              />
            </Tooltip>
          </Grid>
          <Grid item my={0.5}>
            <Divider orientation="vertical" />
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToPrivateTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToPrivateCreditCardsModalOpen}
                disabled={isMoveToPrivateDisabled}
                variant={SeverityEnum.Error}
              >
                <MoveIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToEmployeeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToEmployeeCreditCardsModalOpen}
                disabled={isMoveToEmployeeDisabled}
              >
                <MoveToEmployeeIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={moveToGroupTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleMoveToGroupCreditCardsModalOpen}
                disabled={isMoveToGroupDisabled}
              >
                <MoveToGroupIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={shareTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleShareCreditCardsModalOpen}
                disabled={isShareDisabled}
              >
                <ShareIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={removeTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDetachCreditCardsModalOpen}
                disabled={isRemoveDisabled}
                variant={SeverityEnum.Error}
              >
                <ErrorIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
          <Grid item alignSelf="center">
            <Tooltip title={deleteTooltipTitle}>
              <ActionButton
                width={40}
                height={40}
                onClick={handleDeleteCreditCardsModalOpen}
                disabled={isDeleteDisabled}
                variant={SeverityEnum.Error}
              >
                <DeleteIcon />
              </ActionButton>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid item xs={8} container justifyContent="flex-end" flexWrap="nowrap">
          <Grid item>
            <SearchableTextField
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('common.search')}
            />
          </Grid>
          <Box mr={3} />
          <Grid item>
            <Button
              icon={<PlusIcon />}
              onClick={handleCreateCreditCardModalOpen}
            >
              {t('creditCardsTab.createCreditCardButton')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <OrganizationCreditCardsTable
        selectedCreditCards={selectedCreditCards}
        {...restTableProps}
      />
      <OrganizationCreditCardsTabFilter
        isOpen={isFilterOpen}
        anchorEl={filterAnchorEl}
        onSubmit={handleFilterSubmit}
        onClose={handleFilterClose}
        initialValues={filterValues}
      />
      {isMoveToPrivateCreditCardsModalOpen && (
        <PromptModal
          open={isMoveToPrivateCreditCardsModalOpen}
          onClose={handleMoveToPrivateCreditCardsModalClose}
          onSubmit={handleMoveToPrivateCreditCards}
          title={t(
            'moveCreditCardsToPrivateKeychainModal.title',
            { count: selectedCreditCards.size },
          )}
          description={moveToPrivateCreditCardsModalDescriptionElement}
          list={selectedCreditCardsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isMoveToPrivateCreditCardsLoading}
          approvalButtonText="common.move"
          approvalButtonColor="primary"
        />
      )}
      {isMoveToEmployeeCreditCardsModalOpen && (
        <MoveVaultsToEmployeeModal
          isOpen={isMoveToEmployeeCreditCardsModalOpen}
          onClose={handleMoveToEmployeeCreditCardsModalClose}
          vaults={selectedCreditCardsAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.CreditCard}
        />
      )}
      {isMoveToGroupCreditCardsModalOpen && (
        <MoveVaultsToGroupModal
          isOpen={isMoveToGroupCreditCardsModalOpen}
          onClose={handleMoveToGroupCreditCardsModalClose}
          vaults={selectedCreditCardsAsObjects}
          onSubmit={resetSelectedRows}
          type={VaultTypeEnum.CreditCard}
        />
      )}
      {isShareCreditCardsModalOpen && (
        <ShareVaultsToGroupModal
          isOpen={isShareCreditCardsModalOpen}
          onClose={handleShareCreditCardsModalClose}
          vaults={selectedCreditCardsAsObjects}
          type={VaultTypeEnum.CreditCard}
          onSubmit={resetSelectedRows}
        />
      )}
      {isDetachCreditCardsModalOpen && (
        <PromptModal
          open={isDetachCreditCardsModalOpen}
          onClose={handleDetachCreditCardsModalClose}
          onSubmit={handleDetachCreditCard}
          title={t(
            `organizationCreditCardsTab.detachCreditCardModal.detachFrom${ownership}.title`,
            { count: selectedCreditCards.size },
          )}
          description={t(
            `organizationCreditCardsTab.detachCreditCardModal.detachFrom${ownership}.description`,
          )}
          additionalDescription={t(
            // eslint-disable-next-line max-len
            `organizationCreditCardsTab.detachCreditCardModal.detachFrom${ownership}.additionalDescription`,
          )}
          list={selectedCreditCardsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDetachCreditCardsLoading}
        />
      )}
      {isDeleteCreditCardsModalOpen && (
        <PromptModal
          open={isDeleteCreditCardsModalOpen}
          onClose={handleDeleteCreditCardsModalClose}
          onSubmit={handleDeleteCreditCard}
          title={t(
            'organizationCreditCardsTab.deleteCreditCardModal.title',
            { count: selectedCreditCards.size },
          )}
          description={t(
            'organizationCreditCardsTab.deleteCreditCardModal.description',
          )}
          approvalButtonText="common.delete"
          list={selectedCreditCardsAsObjects}
          renderField="name"
          renderKey="vaultId"
          isLoading={isDeleteCreditCardsLoading}
        />
      )}
      {isCreateCreditCardModalOpen && (
        <CreateCreditCardModal
          isOpen={isCreateCreditCardModalOpen}
          onClose={handleCreateCreditCardModalClose}
          onSubmit={handleCreateCreditCard}
          isLoading={isCreateCreditCardLoading}
        />
      )}
    </PanelContent>
  );
};

export default OrganizationCreditCardsTab;
